import { CalendarIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import type { PropsBase, PropsSingle } from 'react-day-picker';
import { formatLongDate } from '../../lib/date-helpers.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from './button.tsx';
import { Calendar } from './calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx';
import { Text, type TextProps } from './text.tsx';

export type DatePickerProps = {
    value: Date | undefined;
    valuePrefix?: string;
    onChange: (date: Date | undefined) => void;
    disableIcon?: boolean;
    textColor?: TextProps['color'];
    clearable?: boolean;
} & PropsSingle &
    PropsBase;

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    valuePrefix = '',
    disableIcon = false,
    className,
    textColor = 'neutral-900',
    clearable = false,
    ...props
}: DatePickerProps) => {
    const [open, setOpen] = React.useState(false);
    const { t, locale } = useLocale();

    const handleSelect = (date: Date | undefined) => {
        onChange(date);
        setOpen(false);
    };

    const defaultMonth = props.defaultMonth ?? (value ? new Date(value) : new Date());
    const showClear = clearable && value !== undefined && props.disabled !== true;

    return (
        <div className="relative inline-flex">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        className={cn('justify-start text-left font-normal', showClear && 'pr-8', className)}
                    >
                        {!disableIcon && <CalendarIcon className="h-4 w-4 text-neutral-500" />}
                        <Text color={textColor}>
                            {value
                                ? `${valuePrefix}${formatLongDate(value, locale)}`
                                : t('placeholder__select_date')}
                        </Text>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                    <Calendar
                        {...props}
                        mode="single"
                        selected={value}
                        onSelect={handleSelect}
                        defaultMonth={defaultMonth}
                    />
                </PopoverContent>
            </Popover>
            {showClear && (
                <Button
                    variant="ghost"
                    icon={XIcon}
                    className="absolute right-1 top-1/2 -translate-y-1/2 shrink-0 p-0 h-6 w-6 min-w-6 opacity-50 hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onChange(undefined);
                    }}
                />
            )}
        </div>
    );
};
